import "zod/compile";
import { z } from "zod";
import { reportNumberValue } from "./reportAggregate";

/**
 * Report filters: controls above a Report's grid, each bound to a column by
 * name, that narrow every component whose dataset has that column (as a
 * Metabase dashboard filter or a Looker Studio control does). A component
 * whose dataset lacks the column is not filtered.
 *
 * The saved value is each filter's default; a viewer can change it for
 * themselves while viewing. One implementation ({@link reportFilterMatches})
 * serves both data paths — the browser filters rows within the Client row
 * limit, the server filters a Result before Server aggregation — so the two
 * can never disagree.
 */

export const REPORT_MAX_FILTERS = 8;
export const REPORT_FILTER_MAX_VALUES = 200;

const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Use YYYY-MM-DD.");

export const REPORT_DATE_PRESETS = [
  "last-7-days",
  "last-30-days",
  "last-90-days",
  "this-month",
  "last-month",
  "this-quarter",
  "this-year",
  "last-year",
] as const;
export type ReportDatePreset = (typeof REPORT_DATE_PRESETS)[number];

const filterBase = {
  id: z.string().min(1).max(64),
  /** The column filtered, by name, in every dataset that has it. */
  column: z.string().min(1).max(200),
  /** Shown on the control; absent or blank = the column's name. */
  label: z.string().max(80).optional(),
};

export const reportFilterSchema = z.discriminatedUnion("kind", [
  z.object({
    ...filterBase,
    /** Keeps rows whose value, as text, is one of `values`; none = every row. `""` is Empty. */
    kind: z.literal("values"),
    values: z
      .array(z.string().max(500))
      .max(REPORT_FILTER_MAX_VALUES)
      .default([]),
  }),
  z.object({
    ...filterBase,
    /** Keeps rows on or between the dates; a relative `preset` wins over `from`/`to`. */
    kind: z.literal("date-range"),
    preset: z.enum(REPORT_DATE_PRESETS).optional(),
    from: isoDate.optional(),
    to: isoDate.optional(),
  }),
  z.object({
    ...filterBase,
    /** Keeps rows whose Number is at least `min` and at most `max`. */
    kind: z.literal("number-range"),
    min: z.number().finite().optional(),
    max: z.number().finite().optional(),
  }),
]);
export type ReportFilter = z.infer<typeof reportFilterSchema>;
export type ReportFilterKind = ReportFilter["kind"];

export const reportFiltersSchema = z
  .array(reportFilterSchema)
  .max(REPORT_MAX_FILTERS);

/** A cell as either data path holds it: typed in the browser, text on the server. */
export type ReportFilterValue = string | number | boolean | null;

function isoDay(date: Date): string {
  return date.toISOString().slice(0, 10);
}

function utcDate(year: number, month: number, day: number): Date {
  return new Date(Date.UTC(year, month, day));
}

/** The dates a preset covers, relative to `today` (a UTC calendar day). */
export function reportDatePresetRange(
  preset: ReportDatePreset,
  today: Date,
): { from: string; to: string } {
  const y = today.getUTCFullYear();
  const m = today.getUTCMonth();
  const d = today.getUTCDate();
  const daysBack = (days: number) => ({
    from: isoDay(utcDate(y, m, d - (days - 1))),
    to: isoDay(utcDate(y, m, d)),
  });
  switch (preset) {
    case "last-7-days":
      return daysBack(7);
    case "last-30-days":
      return daysBack(30);
    case "last-90-days":
      return daysBack(90);
    case "this-month":
      return {
        from: isoDay(utcDate(y, m, 1)),
        to: isoDay(utcDate(y, m + 1, 0)),
      };
    case "last-month":
      return {
        from: isoDay(utcDate(y, m - 1, 1)),
        to: isoDay(utcDate(y, m, 0)),
      };
    case "this-quarter": {
      const start = m - (m % 3);
      return {
        from: isoDay(utcDate(y, start, 1)),
        to: isoDay(utcDate(y, start + 3, 0)),
      };
    }
    case "this-year":
      return { from: `${y}-01-01`, to: `${y}-12-31` };
    case "last-year":
      return { from: `${y - 1}-01-01`, to: `${y - 1}-12-31` };
  }
}

/**
 * A filter with its relative preset turned into dates, as both data paths
 * apply it. The browser resolves presets and sends dates, so the server's
 * clock never decides which rows a viewer sees.
 */
export function resolveReportFilter(
  filter: ReportFilter,
  today: Date,
): ReportFilter {
  if (filter.kind !== "date-range" || !filter.preset) return filter;
  const { preset, ...rest } = filter;
  return { ...rest, ...reportDatePresetRange(preset, today) };
}

/** Whether a filter narrows anything: a value list with values, or a range with a bound. */
export function isReportFilterActive(filter: ReportFilter): boolean {
  switch (filter.kind) {
    case "values":
      return filter.values.length > 0;
    case "date-range":
      return Boolean(filter.preset || filter.from || filter.to);
    case "number-range":
      return filter.min !== undefined || filter.max !== undefined;
  }
}

function asText(value: ReportFilterValue): string {
  return value === null ? "" : String(value);
}

function asDay(value: ReportFilterValue): string | null {
  if (typeof value !== "string") return null;
  const day = value.slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(day) ? day : null;
}

/**
 * Whether a value passes a resolved filter. An inactive filter passes every
 * value; an active range never passes an Empty or unreadable value.
 */
export function reportFilterMatches(
  filter: ReportFilter,
  value: ReportFilterValue,
): boolean {
  if (!isReportFilterActive(filter)) return true;
  switch (filter.kind) {
    case "values":
      return filter.values.includes(asText(value));
    case "number-range": {
      const number = reportNumberValue(value);
      if (number === null) return false;
      return (
        (filter.min === undefined || number >= filter.min) &&
        (filter.max === undefined || number <= filter.max)
      );
    }
    case "date-range": {
      const day = asDay(value);
      if (day === null) return false;
      return (
        (!filter.from || day >= filter.from) && (!filter.to || day <= filter.to)
      );
    }
  }
}

/** The active filters that apply to a dataset with these columns. */
export function reportFiltersFor(
  filters: readonly ReportFilter[],
  columns: readonly string[],
): ReportFilter[] {
  const known = new Set(columns);
  return filters.filter(
    (filter) => known.has(filter.column) && isReportFilterActive(filter),
  );
}

/**
 * Rows keyed by column name that pass every filter applying to `columns`.
 * Returns `rows` itself when no filter applies, so callers can memoize on it.
 */
export function applyReportFilters<
  Row extends Record<string, ReportFilterValue>,
>(
  rows: readonly Row[],
  filters: readonly ReportFilter[],
  columns: readonly string[],
): readonly Row[] {
  const applying = reportFiltersFor(filters, columns);
  if (!applying.length) return rows;
  return rows.filter((row) =>
    applying.every((filter) =>
      reportFilterMatches(filter, row[filter.column] ?? null),
    ),
  );
}
